import type { AssistantTranscriptEntry, AssistantTranscriptMessage } from "../../api/assistant";

const isMessage = (entry: AssistantTranscriptEntry): entry is AssistantTranscriptMessage => entry.kind === "message";

export const latestTurnReplyIndexes = (entries: Array<AssistantTranscriptEntry>) => {
  const turnStart = lastOfficerActIndex(entries) + 1;
  return entries
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry, index }) => index >= turnStart && isMessage(entry) && entry.role === "assistant")
    .map(({ index }) => index);
};

const lastOfficerActIndex = (entries: Array<AssistantTranscriptEntry>) => {
  for (let index = entries.length - 1; index >= 0; index--) {
    const entry = entries[index];
    if (
      (isMessage(entry) && entry.role === "user") ||
      entry.kind === "proposalConfirmed" ||
      entry.kind === "proposalWithdrawn"
    )
      return index;
  }
  return -1;
};

const withKeys = (entries: Array<AssistantTranscriptEntry>) => {
  const occurrences = new Map<string, number>();

  return entries.map(entry => {
    const content =
      entry.kind === "message"
        ? `${entry.role}:${entry.text}`
        : entry.kind === "proposalConfirmed"
          ? `confirmed:${entry.changes ? "changes" : "proposal"}`
          : entry.kind === "proposalWithdrawn"
            ? "withdrawn"
            : entry.kind === "submitted"
              ? `submitted:${entry.at ?? ""}`
              : `record:${entry.record.recordId}`;
    const occurrence = (occurrences.get(content) ?? 0) + 1;
    occurrences.set(content, occurrence);

    return { ...entry, key: `${content}#${occurrence}` };
  });
};

type KeyedEntry = ReturnType<typeof withKeys>[number];
type KeyedRecordEntry = Extract<KeyedEntry, { kind: "recordCreated" | "recordUpdated" }>;

export type TranscriptSegment =
  | { kind: "entry"; key: string; entry: Exclude<KeyedEntry, KeyedRecordEntry>; index: number }
  | { kind: "records"; key: string; records: Array<KeyedRecordEntry["record"]> };

const isRecordEntry = (entry: KeyedEntry): entry is KeyedRecordEntry =>
  entry.kind === "recordCreated" || entry.kind === "recordUpdated";

export const transcriptSegments = (entries: Array<AssistantTranscriptEntry>) =>
  withKeys(entries).reduce<Array<TranscriptSegment>>((segments, entry, index) => {
    const previous = segments.at(-1);
    if (!isRecordEntry(entry)) segments.push({ kind: "entry", key: entry.key, entry, index });
    else if (previous?.kind === "records") previous.records.push(entry.record);
    else segments.push({ kind: "records", key: entry.key, records: [entry.record] });
    return segments;
  }, []);
